// Orders history list page at /orders.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { apiGet, apiDelete } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { useAuthStore } from "@/lib/auth/store";
import { toast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";

interface Order {
  id: string;
  code: string;
  status: string;
  total: string;
  paidAmount: string;
  paymentMethod?: string | null;
  cashierEmail?: string | null;
  createdAt: string;
  paidAt?: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  draft: "Nháp",
  pending_payment: "Chờ TT",
  paid: "Đã thanh toán",
  partial_paid: "Thanh toán một phần",
  voided: "Đã huỷ",
  cancelled: "Đã hủy bỏ",
};

function fmtVND(s: string) {
  const n = parseFloat(s);
  if (isNaN(n)) return "0 ₫";
  return new Intl.NumberFormat("vi-VN").format(n) + " ₫";
}

function fmtDate(s: string) {
  return new Date(s).toLocaleString("vi-VN");
}

export function OrdersListPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(0);
  const limit = 20;

  const params = {
    limit,
    offset: page * limit,
    ...(code ? { code } : {}),
    ...(status ? { status } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
  };

  const { data, isFetching } = useQuery({
    queryKey: ["orders", params],
    queryFn: () => apiGet<Order[]>("/v1/orders", params),
    placeholderData: (prev) => prev,
  });

  const orders = data ?? [];

  // Draft orders may be hard-deleted by manager/admin (BE enforces both the
  // permission and the draft-only guard).
  const role = useAuthStore((s) => s.user?.role);
  const canDelete = role === "admin" || role === "manager";
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: (id: string) => apiDelete(`/v1/orders/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Đã xóa hóa đơn nháp");
    },
    onError: (e) => toast.error(translateApiError(e)),
  });

  const colCount = canDelete ? 7 : 6;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Đơn hàng</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Tìm mã đơn..."
          value={code}
          onChange={(e) => { setCode(e.target.value); setPage(0); }}
          className="w-44"
        />
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          className="rounded-md border bg-card px-3 py-2 text-sm"
        >
          <option value="">Tất cả trạng thái</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
          className="w-40"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
          className="w-40"
        />
      </div>

      <Table>
        <THead>
          <Tr>
            <Th>Mã đơn</Th>
            <Th>Trạng thái</Th>
            <Th>Thu ngân</Th>
            <Th className="text-right">Tổng</Th>
            <Th>Thanh toán</Th>
            <Th>Thời gian</Th>
            {canDelete && <Th className="text-right">Thao tác</Th>}
          </Tr>
        </THead>
        <TBody>
          {isFetching && (
            <Tr><Td colSpan={colCount} className="text-center text-muted-foreground">Đang tải...</Td></Tr>
          )}
          {!isFetching && orders.length === 0 && (
            <Tr><Td colSpan={colCount} className="text-center text-muted-foreground">Không có đơn hàng</Td></Tr>
          )}
          {orders.map((o) => (
            <Tr
              key={o.id}
              className="cursor-pointer"
              onClick={() => navigate({ to: `/orders/${o.id}` })}
            >
              <Td className="font-mono font-semibold">{o.code}</Td>
              <Td>
                <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium
                  ${o.status === "paid" ? "bg-green-100 text-green-800" :
                    o.status === "voided" ? "bg-red-100 text-red-800" :
                    "bg-muted text-muted-foreground"}`}>
                  {STATUS_LABEL[o.status] ?? o.status}
                </span>
              </Td>
              <Td className="text-sm">{o.cashierEmail ?? "—"}</Td>
              <Td className="text-right font-semibold">{fmtVND(o.total)}</Td>
              <Td className="text-sm">{o.paymentMethod ?? "—"}</Td>
              <Td className="text-sm text-muted-foreground">{fmtDate(o.createdAt)}</Td>
              {canDelete && (
                <Td className="text-right">
                  {o.status === "draft" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      disabled={del.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Xóa hóa đơn nháp ${o.code}?`)) del.mutate(o.id);
                      }}
                      aria-label={`Xóa hóa đơn nháp ${o.code}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </Td>
              )}
            </Tr>
          ))}
        </TBody>
      </Table>

      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
          Trước
        </Button>
        <span className="text-sm text-muted-foreground">Trang {page + 1}</span>
        <Button variant="outline" size="sm" disabled={orders.length < limit} onClick={() => setPage((p) => p + 1)}>
          Tiếp
        </Button>
      </div>
    </div>
  );
}
